import Swiper from '@vendor/swiper';

export { Swiper };

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Reads a CSS custom property duration token and returns milliseconds.
 *
 * @param {string} tokenName
 * @param {number} fallback
 * @returns {number}
 */
export function readMotionDurationMs(tokenName, fallback) {
  const rawValue = getComputedStyle(document.documentElement).getPropertyValue(tokenName).trim();

  if (!rawValue) return fallback;

  if (rawValue.endsWith('ms')) return Number.parseFloat(rawValue);
  if (rawValue.endsWith('s')) return Number.parseFloat(rawValue) * 1000;

  const parsed = Number.parseFloat(rawValue);

  return Number.isFinite(parsed) ? parsed : fallback;
}

/**
 * Resolves Swiper transition speed from theme motion tokens.
 *
 * @param {number} [fallback=300]
 * @returns {number}
 */
export function getThemeSwiperSpeed(fallback = 300) {
  if (window.matchMedia(REDUCED_MOTION_QUERY).matches) return 0;

  return readMotionDurationMs('--token-motion-duration-surface', fallback);
}

/**
 * Shared defaults for MUSTER.GAMLITZ carousels powered by Swiper.
 *
 * @type {import('swiper/types').SwiperOptions}
 */
export const themeSwiperDefaults = {
  slidesPerView: 1,
  spaceBetween: 0,
  watchOverflow: true,
  watchSlidesProgress: true,
  a11y: {
    enabled: true,
    prevSlideMessage: '',
    nextSlideMessage: '',
    firstSlideMessage: '',
    lastSlideMessage: '',
    paginationBulletMessage: '',
  },
  keyboard: {
    enabled: true,
    onlyInViewport: true,
  },
};

/**
 * Creates a Swiper instance with theme motion + accessibility defaults.
 *
 * @param {HTMLElement | string} element
 * @param {import('swiper/types').SwiperOptions} [options]
 * @returns {Swiper}
 */
export function createThemeSwiper(element, options = {}) {
  const speed = getThemeSwiperSpeed(options.speed);

  return new Swiper(element, {
    ...themeSwiperDefaults,
    speed,
    ...options,
    a11y: {
      ...themeSwiperDefaults.a11y,
      ...options.a11y,
    },
    keyboard: {
      ...themeSwiperDefaults.keyboard,
      ...options.keyboard,
    },
  });
}

/**
 * Safely destroys a Swiper instance.
 *
 * @param {Swiper | null | undefined} swiper
 */
export function destroyThemeSwiper(swiper) {
  if (!swiper || swiper.destroyed) return;

  swiper.destroy(true, true);
}

/**
 * Binds prev/next buttons and a numeric counter to a Swiper instance.
 *
 * @param {Swiper} swiper
 * @param {{
 *   previousButton?: HTMLButtonElement | null;
 *   nextButton?: HTMLButtonElement | null;
 *   currentElement?: HTMLElement | null;
 *   totalElement?: HTMLElement | null;
 *   statusElement?: HTMLElement | null;
 *   statusTemplate?: string;
 * }} controls
 */
export function bindThemeSwiperControls(swiper, controls) {
  const {
    previousButton,
    nextButton,
    currentElement,
    totalElement,
    statusElement,
    statusTemplate = '[index] / [length]',
  } = controls;

  const getSlideTotal = () => {
    if (!swiper.params.loop) return swiper.slides.length;

    const slideIndexes = new Set(
      swiper.slides
        .map((slide) => slide.getAttribute('data-swiper-slide-index'))
        .filter((index) => index !== null)
    );

    return slideIndexes.size || swiper.slides.length;
  };

  const updateCounter = () => {
    const currentIndex = swiper.params.loop ? swiper.realIndex : swiper.activeIndex;
    const currentValue = currentIndex + 1;
    const totalValue = getSlideTotal();
    const shouldDisableAtEdges = !swiper.params.loop && !swiper.params.rewind;

    if (currentElement) currentElement.textContent = String(currentValue);
    if (totalElement) totalElement.textContent = String(totalValue);

    if (statusElement) {
      statusElement.textContent = statusTemplate
        .replace('[index]', String(currentValue))
        .replace('[length]', String(totalValue));
    }

    if (previousButton) {
      previousButton.disabled = swiper.isLocked || (shouldDisableAtEdges && swiper.isBeginning);
    }

    if (nextButton) {
      nextButton.disabled = swiper.isLocked || (shouldDisableAtEdges && swiper.isEnd);
    }
  };

  previousButton?.addEventListener('click', () => swiper.slidePrev());
  nextButton?.addEventListener('click', () => swiper.slideNext());

  swiper.on('init', updateCounter);
  swiper.on('slideChange', updateCounter);
  swiper.on('resize', updateCounter);
  swiper.on('update', updateCounter);

  updateCounter();
}

/**
 * Re-initializes Swiper when Shopify section blocks are re-rendered in the editor.
 *
 * @param {HTMLElement} host
 * @param {() => Swiper | void} init
 * @returns {() => void}
 */
export function bindShopifySectionSwiper(host, init) {
  /** @type {Swiper | undefined} */
  let swiper;

  const mount = () => {
    destroyThemeSwiper(swiper);
    swiper = init() ?? undefined;
  };

  mount();

  const handleEditorSectionRefresh = () => {
    mount();
  };

  const handleBlockSelect = (event) => {
    const slide = event.target.closest('.swiper-slide');

    if (!slide || !host.contains(slide) || !swiper) return;

    const loopIndex = slide.getAttribute('data-swiper-slide-index');

    if (swiper.params.loop && loopIndex !== null) {
      const index = Number.parseInt(loopIndex, 10);

      if (Number.isFinite(index)) {
        swiper.slideToLoop(index);
        return;
      }
    }

    const index = swiper.slides.indexOf(slide);

    if (index >= 0) {
      swiper.slideTo(index);
    }
  };

  host.addEventListener('theme:editor:section:refresh', handleEditorSectionRefresh);
  host.addEventListener('shopify:block:select', handleBlockSelect);

  return () => {
    host.removeEventListener('theme:editor:section:refresh', handleEditorSectionRefresh);
    host.removeEventListener('shopify:block:select', handleBlockSelect);
    destroyThemeSwiper(swiper);
  };
}
